import * as RProgress from '@radix-ui/react-progress'
import {raise} from '@translated/utils'
import clsx from 'clsx'
import React, {createContext, useContext, type ComponentProps} from 'react'

const ProgressContext = createContext<{value?: number} | null>(null)

const useProgressContext = () =>
  useContext(ProgressContext) ?? raise('Progress context is missing.')

export const Progress = ({
  value,
  animate = true,
  className,
  ...props
}: ComponentProps<typeof RProgress.Root> & {animate?: boolean}) => {
  const [progress, setProgress] = React.useState(0)

  React.useEffect(() => {
    if (value == null) return
    const timer = setTimeout(() => setProgress(Math.min(value, 100)), 500)
    return () => clearTimeout(timer)
  }, [value])

  return (
    <RProgress.Root
      className={clsx(
        'relative overflow-hidden rounded-sm w-full h-1',
        className,
      )}
      style={{
        // Fix overflow clipping in Safari
        // https://gist.github.com/domske/b66047671c780a238b51c51ffde8d3a0
        transform: 'translateZ(0)',
      }}
      value={animate ? progress : value}
      {...props}
    >
      <ProgressContext.Provider
        value={{value: animate ? progress : value || 0}}
      >
        {props.children}
      </ProgressContext.Provider>
    </RProgress.Root>
  )
}

export const ProgressIndicator = ({
  className,
  style,
  ...props
}: ComponentProps<typeof RProgress.Indicator>) => {
  const value = useProgressContext()
  return (
    <RProgress.Indicator
      className={clsx(
        'w-full h-full transition-transform duration-[660ms] ease-[cubic-bezier(0.65, 0, 0.35, 1)]',
        className,
      )}
      style={{
        transform: `translateX(-${100 - (value?.value ?? 0)}%)`,
        ...style,
      }}
      {...props}
    />
  )
}
